'use client'

import {
  Badge,
  Button,
  InputForm,
  LoadingDots,
  PriceBadge,
  QtyBadge,
  ScrollArea,
  SkuBadge,
  Switch,
} from '@/components/ui'
import { getOrder, submitReturnOrder } from '@/lib/actions'
import { useModals, useSearchParams } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import React, { useTransition } from 'react'
import { useFormStatus } from 'react-dom'
import { CheckCircle } from 'solar-icons'
import { toast } from 'sonner'

interface ReturnOrderProps extends React.ComponentPropsWithoutRef<'div'> {}

export const ReturnOrder = ({ className }: ReturnOrderProps) => {
  const { closeModal, openModal } = useModals()
  const { setSearchParams } = useSearchParams()
  const [isPendingOrderDetails, startOrderDetailsTransition] = useTransition()
  const [isPendingReturnSubmit, startSubmitReturnTransition] = useTransition()
  const [details, setDetails] = React.useState<any>(null)
  const [returningItems, setReturningItems] = React.useState<
    {
      order_item_id: string
      qty?: number
      will_restock?: boolean
      return_amount?: number
    }[]
  >([])

  const handleOrderSearch = async (data: FormData) => {
    const orderNumber = data.get('order-number') as string

    if (!orderNumber) {
      toast.error('Invalid order number')
      return
    }

    startOrderDetailsTransition(async () => {
      try {
        const res = await getOrder(orderNumber)
        setDetails(res)
      } catch (error: any) {
        toast.error('Could not find order with that number.')
        setDetails(null)
      }
    })
  }

  const handleReturn = async (data: FormData) => {
    if (!hasReturnableItems) {
      toast.error('No returns available')
      return
    }

    startSubmitReturnTransition(async () => {
      try {
        const result = await submitReturnOrder({
          order_id: details?.order?.id,
          order_items: returningItems,
        })
        toast.success('Return submitted successfully')

        // Set the order return ID in search params and open print modal
        if (result?.order_return?.id) {
          const returnId = String(result.order_return.id)
          setSearchParams({ orderReturnId: returnId })
          closeModal('RETURN_ORDER')
          // Small delay to ensure state propagates
          setTimeout(() => {
            openModal('PRINT_RETURN_RECEIPT')
          }, 100)
        } else {
          closeModal('RETURN_ORDER')
        }
      } catch (error: any) {
        toast.error(
          'This order cannot be returned. It is already fully returned or has no returns available.',
        )
      }
    })
  }

  const onReturnQtyChange = (order_item_id: string, qty: number) => {
    if (qty === 0 || !qty) {
      setReturningItems((prev) =>
        prev.filter((item) => item.order_item_id !== order_item_id),
      )
      return
    }

    setReturningItems((prev) => {
      const index = prev.findIndex(
        (item) => item.order_item_id === order_item_id,
      )
      if (index === -1) {
        return [...prev, { order_item_id, qty, will_restock: false }]
      }

      const updatedItems = [...prev]
      updatedItems[index].qty = qty
      return updatedItems
    })
  }

  const onRestockChange = (order_item_id: string, will_restock: boolean) => {
    if (!returningItems.find((item) => item.order_item_id === order_item_id)) {
      return
    }

    setReturningItems((prev) => {
      const index = prev.findIndex(
        (item) => item.order_item_id === order_item_id,
      )
      if (index === -1) {
        return [...prev, { order_item_id, will_restock }]
      }

      const updatedItems = [...prev]
      updatedItems[index].will_restock = will_restock
      return updatedItems
    })
  }

  const onReturnAmountChange = (
    order_item_id: string,
    return_amount: number,
  ) => {
    if (!returningItems.find((item) => item.order_item_id === order_item_id)) {
      return
    }

    setReturningItems((prev) => {
      const index = prev.findIndex(
        (item) => item.order_item_id === order_item_id,
      )
      if (index === -1) {
        return prev
      }

      const updatedItems = [...prev]
      updatedItems[index].return_amount = return_amount
      return updatedItems
    })
  }

  const hasReturnableItems = React.useMemo(() => {
    return details?.order_items?.some((product: any) => {
      const returnableQty =
        Number(product?.sold_quantity) - Number(product?.return_quantity)
      return returnableQty > 0
    })
  }, [details?.order_items])

  return (
    <div className={cn('flex w-full flex-col gap-5', className)}>
      <form
        action={handleOrderSearch}
        className={cn('flex w-full flex-col gap-5', className)}
      >
        <div className="flex flex-col gap-2">
          <InputForm
            autoCorrect="off"
            autoComplete="off"
            maxLength={4}
            name="order-number"
            placeholder="Enter order number"
            className="lg:h-[44px]"
            submitLabel="Find Order"
            autoFocus
          />
        </div>
      </form>

      <form
        action={handleReturn}
        className={cn('flex w-full flex-col gap-5', className)}
      >
        {isPendingOrderDetails && (
          <div className="flex h-[300px] w-full items-center justify-center lg:h-[400px]">
            <LoadingDots />
          </div>
        )}

        {!isPendingOrderDetails && !details?.order_items && (
          <div className="flex h-[300px] w-full items-center justify-center rounded-lg border border-gray-100 text-sm text-gray-700 lg:h-[400px]">
            No order details.
          </div>
        )}
        {!isPendingOrderDetails && details?.order_items && (
          <div className="flex w-full flex-col gap-3">
            <ScrollArea className="h-auto lg:h-[400px]">
              <div className="flex w-full flex-col gap-1">
                {details?.order_items?.map((product: any, index: number) => {
                  const returnableQty =
                    Number(product?.sold_quantity) -
                    Number(product?.return_quantity)

                  return (
                    <div
                      key={index}
                      className={cn(
                        'group flex flex-col gap-4 rounded-lg border border-gray-100 p-3 shadow-card focus-within:bg-gray-50 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-800 lg:flex-row lg:items-center lg:justify-between dark:border-gray-700',
                        className,
                      )}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="meta flex flex-grow flex-col gap-1">
                          <h2 className="font-medium">
                            {product.product_name}
                          </h2>

                          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-5">
                            <PriceBadge
                              price={product?.total}
                              badge={{ variant: 'default' }}
                              className="text-gray-700"
                            />
                            <SkuBadge className="text-gray-700">
                              {product.sku}
                            </SkuBadge>
                            <QtyBadge className="text-gray-700">
                              {product.sold_quantity}
                            </QtyBadge>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {returningItems.find(
                          (item) => item.will_restock === product?.id,
                        ) && (
                          <div className="flex">
                            <CheckCircle className="size-5 text-green-500" />
                          </div>
                        )}

                        {returnableQty > 0 && (
                          <div className="flex w-full flex-col gap-2 lg:w-[unset] lg:flex-row lg:items-center">
                            <div className="relative w-full lg:w-[120px]">
                              <select
                                title="Return Qty"
                                name={`return-${product?.id}`}
                                className="flex h-10 w-full appearance-none items-center justify-between whitespace-nowrap rounded-md border border-gray-100 bg-transparent bg-white px-3 py-2 text-sm shadow-card ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-950 disabled:cursor-not-allowed disabled:opacity-50"
                                onChange={(e) =>
                                  onReturnQtyChange(
                                    product?.id,
                                    Number(e.target.value),
                                  )
                                }
                              >
                                <option value="0">Select Qty</option>
                                {Array.from({
                                  length: returnableQty,
                                }).map((_, index) => (
                                  <option
                                    key={index}
                                    value={(index + 1).toString()}
                                  >
                                    {index + 1}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 transform" />
                            </div>

                            <div className="relative w-full lg:w-[140px]">
                              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                                Rs.
                              </span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                title="Return Amount"
                                name={`return-amount-${product?.id}`}
                                placeholder="0.00"
                                disabled={
                                  !returningItems.find(
                                    (item) =>
                                      item.order_item_id === product?.id,
                                  )
                                }
                                className="flex h-10 w-full appearance-none items-center justify-between whitespace-nowrap rounded-md border border-gray-100 bg-transparent bg-white py-2 pl-10 pr-3 text-sm shadow-card ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-950 disabled:cursor-not-allowed disabled:opacity-50"
                                onChange={(e) =>
                                  onReturnAmountChange(
                                    product?.id,
                                    Number(e.target.value),
                                  )
                                }
                              />
                            </div>

                            <label
                              htmlFor={`restock-${product?.id}`}
                              className="flex h-10 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-gray-100 bg-transparent bg-white px-3 py-2 text-sm shadow-card ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-950 disabled:cursor-not-allowed disabled:opacity-50 lg:w-[132px] [&>span]:line-clamp-1"
                            >
                              <span className="shrink-0">Re-stock</span>
                              <Switch
                                name={`restock-${product?.id}`}
                                disabled={
                                  !returningItems.find(
                                    (item) =>
                                      item.order_item_id === product?.id,
                                  )
                                }
                                onCheckedChange={(checked) =>
                                  onRestockChange(product?.id, checked)
                                }
                              />
                            </label>
                          </div>
                        )}

                        {returnableQty === 0 && (
                          <div>
                            <Badge variant="secondary" size="md">
                              No returns available
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </ScrollArea>
          </div>
        )}

        <Actions disabled={isPendingReturnSubmit || !hasReturnableItems} />
      </form>
    </div>
  )
}

interface ActionsProps extends React.ComponentPropsWithoutRef<'div'> {
  disabled?: boolean
}

const Actions = ({ disabled }: ActionsProps) => {
  const { pending } = useFormStatus()
  const { closeModal } = useModals()

  return (
    <div className="flex w-full items-center gap-2">
      <Button
        variant="outline"
        className="w-full"
        size="lg"
        disabled={pending}
        onClick={() => {
          closeModal('RETURN_ORDER')
        }}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={pending || disabled}
      >
        {pending ? 'Submitting' : 'Submit Return'}
      </Button>
    </div>
  )
}
